// Language Context for sQawk - Global language state management
// Now integrated with i18next for better scalability
import { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { translations, getDefaultLanguage } from './translations';
import { changeLanguage as i18nChangeLanguage } from './i18n';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  
  const [language, setLanguage] = useState(() => {
    // Check localStorage first, then browser default
    const saved = localStorage.getItem('sqawk_language');
    return saved || getDefaultLanguage();
  });

  useEffect(() => {
    localStorage.setItem('sqawk_language', language);
    // Update document language attribute for accessibility
    document.documentElement.lang = language;
    // Sync with i18next
    if (i18n.language !== language) {
      i18nChangeLanguage(language);
    }
  }, [language, i18n]);

  // Translation function - uses both local translations and i18next
  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  // Available languages - 12 total for sQawk
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
  ];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
